/**
 * Anatomy as LPC paints it: deliberate marks on simple shapes, not more volumes.
 *
 * Collarbones, a lit deltoid cap, abdominals, a fist's knuckles, a knee -- modelled as bumps these
 * melt into lumps at this size, and shading alone puts a highlight wherever the light happens to
 * fall rather than one per muscle. LPC paints each muscle its own small light and shade, which is
 * what makes every joint read. So these are painted on: pixel patterns read off the LPC male body
 * (tools ember-markgen), each pinned to a joint of the skeleton so it moves with the pose.
 *
 * A mark's `at` is where LPC paints it in the standing frame of its facing; its offset from the
 * joint is measured in Ember's standing pose (default proportions), so standing still the mark lands
 * on LPC's pixel and any other pose carries it along with the joint. It paints only bare skin of the
 * parts around that joint (a shirt or an arm in front hides it), and is left off when the limb has
 * swung far from where it hung, since a pattern does not rotate.
 *
 * Front and back marks are written for the screen-left limb and mirrored; right is left mirrored.
 * Pattern characters: L light, B base, S shade, D deep, n line; "." leaves the render alone.
 */
import { Material } from "./palette"
import { DEFAULT_RASTER, FACING_YAW, Facing, PartGroupList, PixelStamp, RasterOptions, cameraAxes, projectPoint } from "./raster"
import { DEFAULT_PROPORTIONS, LimbJoints, Proportions, Skeleton, restingSample, solvePose } from "./rig"
import { Vec3, apply, dot, mul, rotY } from "./vec"

type Center = "head" | "neck" | "chest" | "pelvis"
type Joint = "shoulder" | "elbow" | "hand" | "knee" | "ankle"
/**
 * Front and back: the limb on the screen's left (a mark on the head or body given this is one of a
 * mirrored pair, like the ears); side-on: the limb nearer or farther from the camera.
 */
type Side = "screenLeft" | "screenRight" | "near" | "far"

interface Mark {
    on: Center | Joint
    side?: Side
    /** Top-left pixel of the pattern in LPC's standing frame of this facing. */
    at: [number, number]
    rows: string[]
}

const MARKS: Record<"front" | "back" | "left", Mark[]> = {
    front: [
        // The crown, lit, its outline softened at the corners.
        { on: "head", at: [25, 16], rows: ["..nDSBBBSSDn..", "nSBBBBBBBBSSDn", "SBBBBBBBBBBBSS"] },
        // The ear.
        { on: "head", side: "screenLeft", at: [22, 26], rows: ["nDSB", "BDSS", "BDSS", "BBDS", ".BDS"] },
        // Collarbones meeting at the sternum, the upper chest shaded under them.
        { on: "chest", at: [25, 33], rows: ["nSBBBBBBBBBBSn", "SnnSBBBBBBSnnS", "BSSnnnnnnnnSSB", "BBSSSSSSSSSSBB", "BBBSSSSSSSSBBB"] },
        // The deltoid, lit on its cap.
        { on: "shoulder", side: "screenLeft", at: [20, 33], rows: [".nSSD", "nLLLB", "SLLLB", "BLLBB", "BBBBB"] },
        // The armpit crease where the arm leaves the chest.
        { on: "shoulder", side: "screenLeft", at: [19, 38], rows: ["DSSDSS.", "SSDnDS.", "SSSnnSS"] },
        // The lower ribs: pecs lit along their bottom edge, flanks in shade.
        { on: "chest", at: [24, 41], rows: ["DSBLBBBBBBBBLBSD", "nDSBLLBBBBLLBSDn", ".DDDSBBBBBBSDDD."] },
        // The abdomen: two pairs of lit blocks.
        { on: "pelvis", at: [25, 44], rows: ["SSSBBLLLLBBSSS", "SSSBLLBBLLBSSS", "SSSSBBBBBBSSSS"] },
        // The lower belly in shade, the lines of the hips running in to the groin.
        { on: "pelvis", at: [24, 47], rows: ["SSDSSS....SSSDSS", "SSDDSSSSSSSSDDSS", "..SDnDSSSSDnDS..", "...SSn....nSS..."] },
        // The forearm, lit along its outer edge.
        { on: "elbow", side: "screenLeft", at: [18, 41], rows: ["LBSD", "LBS.", "LLS.", "BBD."] },
        // The fist: knuckles lit, fingers lined.
        { on: "hand", side: "screenLeft", at: [18, 45], rows: ["SBBS.", "SLBBB", "DBBnB", "nBBnn", ".DBSS"] },
        // The knee: a stroke inside above it, the calf's outline below.
        { on: "knee", side: "screenLeft", at: [23, 50], rows: ["D.....", "D....n", "D...D.", "D...n.", "nD....", "n..D..", "....n."] },
        // The toes.
        { on: "ankle", side: "screenLeft", at: [21, 57], rows: [".SBBBSD", "DBBBBBS", "BBBBBBS", "SBLDLL."] },
    ],
    back: [
        // The crown, lit, its outline softened at the corners.
        { on: "head", at: [25, 16], rows: ["..nSSBBBSSSn..", "nSBBBBBBBBSSSn", "SBBBBBBBBBBSSS"] },
        // The ear.
        { on: "head", side: "screenLeft", at: [22, 24], rows: ["nSBB", "nDBB", "SDSB", "BSSB", "SBSS"] },
        // The nape in the head's shadow, down over the trapezius.
        { on: "neck", at: [24, 29], rows: ["DSSSBBBBBBSSSSSD", "nSSSSSSSSSSSSSSn", ".DSSSSSSSSSSSSD.", ".nDSSSSSSSSSSDn.", "DDnnSSSSSSSSnnSD", "BBSSSSSSSSSSSSBB", "BBBSSSSSSSSSSBBB", "BBBBBSSSSSSBBBBB", "BBBBBBBBBBBBBBBB"] },
        // The deltoid, lit on its cap.
        { on: "shoulder", side: "screenLeft", at: [20, 33], rows: [".nSSD", "nLLLB", "BLLBB", "BBBBB", "SBBBB"] },
        // The shoulder blades.
        { on: "chest", at: [23, 38], rows: ["..................", "DS..............SD", ".DS...S....S...SD.", ".nDS..S....S..SDn."] },
        // The back of the arm, where it leaves the shoulder blade.
        { on: "shoulder", side: "screenLeft", at: [18, 38], rows: [".SSDD", "nSSSn", "D.SD.", "S.Sn."] },
        // The lower back: the spine's valley between muscles in shade.
        { on: "pelvis", at: [24, 42], rows: [".DDDSS....SSDDD.", ".DSSSS....SSSSDn", "nSSSSS....SSSSSn", "nSSSS......SSSSn", "DSDSS......SSDSD"] },
        // The seat.
        { on: "pelvis", at: [23, 47], rows: [".SSDSS......SSDSS.", "...SnSS....SSnS...", "D...DnDDSSDDnD...D", "D...Sn......nS...D"] },
        // The forearm, lit along its outer edge.
        { on: "elbow", side: "screenLeft", at: [18, 40], rows: ["DBSD", "SBSn", "LLS.", "BLD."] },
        // The back of the hand.
        { on: "hand", side: "screenLeft", at: [18, 44], rows: ["SBBS.", "SLBBn", "DBBBS", "nBBBB", ".DBSS"] },
        // The calf, dark, its outline lined.
        { on: "knee", side: "screenLeft", at: [21, 51], rows: ["..D..SD", "..nSSSn", "..nDDS.", ".nnDDD.", "nnDSSS.", "nnS..S."] },
        // The heel, lit.
        { on: "ankle", side: "screenLeft", at: [21, 57], rows: ["DDLLBD.", "DSLLBD.", ".nSBDn."] },
    ],
    left: [
        // The crown, lit, its outline softened at the corners.
        { on: "head", at: [26, 16], rows: ["..nDSBBBSDn...", "nDBBBBBBBBSDn.", "SBBBBBBBBBBSSn"] },
        // The ear, and the angle of the jaw below it.
        { on: "head", at: [34, 26], rows: ["BBDDDBS", "BSDBDSD", "SSBBDSD", "SBBBDD.", "SSBDn..", "SSnn..."] },
        // The face, lit to its edge: a profile is not rimmed in shade like the back of the head.
        { on: "head", at: [24, 19], rows: [".SBB.", "nBBB.", "DBBB.", "SBBBB", "BBBBB", "BBBBB", "BBBBB", "BBBBB", "BBBBB", "SBBBS", ".BBB.", ".BBB.", ".BBB.", ".SBBB"] },
        // Under the jaw: the chin's underside, the jaw's angle, a line and a band of shade on the chest below.
        { on: "neck", at: [25, 32], rows: ["SBBBBBBBBBSD", ".DBBBBBBBSDn", "..nSBBBBSDnn", "nnnnnnnnnnSB", "DDDSSSSSSBBB", "SSSSSSSSBBBB"] },
        // The near deltoid, lit on its cap.
        { on: "shoulder", side: "near", at: [35, 33], rows: ["Dnn....", "nnSSn..", "SBLLLn.", "BBLLLS.", "BBBLLBn"] },
        // The near arm, lit down its front.
        { on: "elbow", side: "near", at: [36, 39], rows: ["SS....", "DDS...", "S.D...", "S..L..", "D..L.S", "D.D..D", ".DD..."] },
        // The near hand.
        { on: "hand", side: "near", at: [36, 46], rows: ["S....", "n..LD", "n...D", "...D."] },
        // Chest and belly: the pec lit along its bottom, the stomach below.
        { on: "chest", at: [25, 40], rows: ["SBBBBBBBBBB", "SBBBBBLLBBB", "nBBBLLLBBSS", ".SBBSSDDSSD", ".DLLBBBSSSD", ".nBBLLBSSSD"] },
        // The lower belly and the groin.
        { on: "pelvis", at: [26, 46], rows: ["n.....SSD.", "nS...SSSD.", "DSSSSSSSS.", "SSDnnDSSS.", "SSDD.nS..n"] },
        // The near knee.
        { on: "knee", side: "near", at: [31, 50], rows: ["nS..n..", ".S..SSn", ".n...Sn", "..D...D", "..nS.SD", "...nSSS"] },
        // The far knee.
        { on: "knee", side: "far", at: [26, 51], rows: ["DSSDn", "nSSSn", ".DSSD", ".nSSD", ".nSSS", "DS.SS"] },
    ],
}

const FRAME = 64
const mirrorRows = (rows: string[]) => rows.map(row => row.split("").reverse().join(""))
/** The same mark on the other half of the frame: LPC's facings mirror about the frame's middle. */
const mirrored = (m: Mark, side: Side | undefined): Mark => ({
    ...m,
    side,
    at: [FRAME - m.at[0] - m.rows[0].length, m.at[1]],
    rows: mirrorRows(m.rows),
})

const marksFor = (facing: Facing): Mark[] => {
    if (facing === "right") return MARKS.left.map(m => mirrored(m, m.side))
    // These screen-space patterns are measured for cardinal views only. Diagonals use volume shading.
    const own: Mark[] | undefined = MARKS[facing]
    if (!own) return []
    return [...own, ...own.filter(m => m.side === "screenLeft").map(m => mirrored(m, "screenRight"))]
}

/** The bone a joint hangs off: a mark on the joint is dropped once that bone swings well away. */
const BONE: Record<Joint, [keyof LimbJoints, keyof LimbJoints]> = {
    shoulder: ["shoulder", "elbow"],
    elbow: ["shoulder", "elbow"],
    hand: ["elbow", "hand"],
    knee: ["hip", "knee"],
    ankle: ["knee", "ankle"],
}
const SWUNG = (35 * Math.PI) / 180
/** How far the torso may turn from its standing turn before its marks stop fitting. */
const TURNED = 0.35

const armOf = (side: "L" | "R") => (side === "L" ? "armL" : "armR")
const legOf = (side: "L" | "R") => (side === "L" ? "legL" : "legR")

export const bodyStamps = (
    sk: Skeleton,
    p: Proportions,
    skin: Material,
    facing: Facing,
    o: RasterOptions = DEFAULT_RASTER,
): PixelStamp[] => {
    const yaw = rotY(FACING_YAW[facing])
    const { back } = cameraAxes(o.pitch)
    const at = (v: Vec3) => projectPoint(apply(yaw, v), o)
    const depth = (v: Vec3) => dot(apply(yaw, v), back)
    const toward = (s: Skeleton) => dot(apply(mul(yaw, s.torsoBasis), [0, 0, 1]), back)
    // Where the marks were measured: Ember standing, in this facing, at the default build.
    const standing = solvePose(restingSample(DEFAULT_PROPORTIONS, facing), DEFAULT_PROPORTIONS)
    const torsoFits = Math.abs(toward(sk) - toward(standing)) < TURNED
    // Which of the skeleton's limbs a side names, decided standing so it cannot swap mid-stride.
    const limb = (side: Side | undefined, joint: Joint): "L" | "R" => {
        const [l, r] = [standing.L[joint], standing.R[joint]]
        if (side === "near") return depth(l) > depth(r) ? "L" : "R"
        if (side === "far") return depth(l) > depth(r) ? "R" : "L"
        const leftmost = at(l).x < at(r).x ? "L" : "R"
        return side === "screenRight" ? (leftmost === "L" ? "R" : "L") : leftmost
    }
    const angle = (s: Skeleton, side: "L" | "R", joint: Joint) => {
        const [from, to] = BONE[joint]
        const a = at(s[side][from]), b = at(s[side][to])
        return Math.atan2(b.y - a.y, b.x - a.x)
    }
    const palette = { L: skin.light, B: skin.base, S: skin.shade, D: skin.deep, n: skin.line }
    const stamps: PixelStamp[] = []
    for (const mark of marksFor(facing)) {
        let now: Vec3, then: Vec3, group: PartGroupList
        if (mark.on === "head" || mark.on === "neck" || mark.on === "chest" || mark.on === "pelvis") {
            if (!torsoFits) continue
            now = sk[mark.on]
            then = standing[mark.on]
            group = mark.on === "head" ? ["head"] : mark.on === "neck" ? ["head", "torso", "shoulder"] : ["torso", "bust", "shoulder"]
        } else {
            const side = limb(mark.side, mark.on)
            let swing = Math.abs(angle(sk, side, mark.on) - angle(standing, side, mark.on))
            if (swing > Math.PI) swing = 2 * Math.PI - swing
            if (swing > SWUNG) continue
            now = sk[side][mark.on]
            then = standing[side][mark.on]
            group =
                mark.on === "shoulder" ? ["shoulder", "torso", armOf(side)] : mark.on === "knee" || mark.on === "ankle" ? [legOf(side)] : [armOf(side)]
        }
        const q = at(now), q0 = at(then)
        // `at` is in LPC's 64px frame; a bigger frame (a held tool's) centres the same body in it.
        const grow = [(o.width - FRAME) / 2, (o.height - FRAME) / 2]
        stamps.push({
            x: Math.round(q.x + mark.at[0] - q0.x + grow[0]),
            y: Math.round(q.y + mark.at[1] - q0.y + grow[1]),
            rows: mark.rows,
            palette,
            group,
            material: skin,
        })
    }
    return stamps
}
